import React from "react"

import { Grid } from "@mui/material"

import {
    ErrorResponse,
    Loading,
    RestResponse,
} from "@sinossi/mates-react-library"

import withUseRouter, { UseRouterParams } from "services/router"
import { WorkflowService } from "services/workflow.service"
import {
    AssignableUsers, AssignRequest,
    NewWorkflowTemplate,
    PaginatedResponse,
    SearchFilter,
    Template,
    TemplateStep,
    WorkflowTemplateSteps,
} from "models"
import {
    MatesComponent,
    MatesComponentState,
} from "components/mates-component"

import {
    DeleteWorkflow,
    AssignTemplate,
    DeleteStep,
    NewStep,
    NewTemplate,
} from "../modals"
import { TemplateColumnOne } from "./template-columns/template-column-one"
import { TemplateColumnTwo } from "./template-columns/template-column-two"

interface TemplateSectionProps extends UseRouterParams {
    handlePagination: (request: SearchFilter) => void
    templatesData: PaginatedResponse<Template>
    usersData: AssignableUsers[]
}
interface TemplateSectionState extends MatesComponentState {
    loading: boolean
    openNewTemplate: boolean
    openDeleteTemplate: boolean
    deleteTemplate: Template | null
    openAssignTemplate: boolean
    assignTemplate: Template | null
    openNewStep: boolean
    openDeleteStep: boolean
    deleteStep: TemplateStep | null
    openEditStep: boolean
    editStep: TemplateStep | null
    templateStepsResponse: WorkflowTemplateSteps | null
    selectedTemplate: Template | null
}
class TemplateSection extends React.Component<TemplateSectionProps, TemplateSectionState> {
    private readonly matesComponent: MatesComponent<TemplateSectionProps, TemplateSectionState> = new MatesComponent(this)

    constructor(props: TemplateSectionProps) {
        super(props);
        this.state = {
            ...this.matesComponent.setupState(),
            loading: false,
            openNewTemplate: false,
            openDeleteTemplate: false,
            deleteTemplate: null,
            openAssignTemplate: false,
            assignTemplate: null,
            openNewStep: false,
            openDeleteStep: false,
            deleteStep: null,
            openEditStep: false,
            editStep: null,
            templateStepsResponse: null,
            selectedTemplate: null,
        }
    }

    private onItemSelected = (template: Template | null) => {
        if (template) {
            this.setState(
                () => ({
                    loading: true,
                    selectedTemplate: template,
                }),
                () => WorkflowService.getTemplateStepsList(
                    template.id,
                    this.getTemplateStepsListCallback,
                    this.matesComponent.onException,
                ),
            )
        }
    }

    private getTemplateStepsListCallback = (response: RestResponse<WorkflowTemplateSteps, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.setState(() => ({
            templateStepsResponse: response.data,
            loading: false
        }))
    }

    private openCreateNewTemplate = () => {
        this.setState(() => ({
            openNewTemplate: true
        }))
    }

    private closeCreateNewTemplate = () => {
        this.setState(() => ({
            openNewTemplate: false,
        }))
    }

    private openDeleteTemplate = (template: Template) => {
        this.setState(() => ({
            openDeleteTemplate: true,
            deleteTemplate: template,
        }))
    }

    private closeDeleteTemplate = () => {
        this.setState(() => ({
            openDeleteTemplate: false,
            deleteTemplate: null,
        }))
    }

    private openAssignTemplate = (template: Template) => {
        this.setState(() => ({
            openAssignTemplate: true,
            assignTemplate: template
        }))
    }

    private closeAssignTemplate = () => {
        this.setState(() => ({
            openAssignTemplate: false,
            assignTemplate: null,
        }))
    }

    private openNewStep = () => {
        this.setState(() => ({
            openNewStep: true,
        }))
    }

    private closeNewStep = () => {
        this.setState(() => ({
            openNewStep: false,
        }))
    }

    private openDeleteStep = (step: TemplateStep) => {
        this.setState(() => ({
            openDeleteStep: true,
            deleteStep: step,
        }))
    }

    private closeDeleteStep = () => {
        this.setState(() => ({
            openDeleteStep: false,
            deleteStep: null,
        }))
    }

    private openEditStep = (step: TemplateStep) => {
        this.setState(() => ({
            openEditStep: true,
            editStep: step
        }))
    }

    private closeEditStep = () => {
        this.setState(() => ({
            openEditStep: false,
            editStep: null,
        }))
    }

    private handlePagination = (value: number) => {
        let request: SearchFilter = {
            pageSize: 10,
            pageId: value - 1,
            orderType: null,
            orderBy: null
        }
        this.props.handlePagination(request)
    }

    private defaultHandlePagination = () => {
        let request: SearchFilter = {
            pageSize: 10,
            pageId: 0,
            orderType: null,
            orderBy: null
        }

        this.setState(() => ({
            loading: false
        }), () => {
            this.props.handlePagination(request)
            this.onItemSelected(this.state.selectedTemplate)
        })
    }

    private createWorkflowFromTemplate = (template: Template) => {

        let request: { match: string | null, asset: string | null } = {
            match: null,
            asset: null
        }

        this.setState(() => ({
            loading: true
        }), () => WorkflowService.createWorkflowFromTemplate(
            template.id,
            request,
            this.createWorkflowFromTemplateCallback,
            this.matesComponent.onException
        )
        )
    }

    private createWorkflowFromTemplateCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.defaultHandlePagination()
    }

    private setDefault = (template: Template) => {
        this.setState(
            () => ({
                loading: true,
            }),
            () => WorkflowService.setWorkflowTemplateDefault(
                template.id,
                this.setDefaultCallback,
                this.matesComponent.onException,
            ),
        )
    }

    private setDefaultCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.defaultHandlePagination()
    }

    private cloneWorkflowTemplate = (template: Template) => {
        this.setState(
            () => ({
                loading: true,
            }),
            () => WorkflowService.cloneWorkflowTemplate(
                template.id,
                this.cloneWorkflowTemplateCallback,
                this.matesComponent.onException,
            ),
        )
    }

    private cloneWorkflowTemplateCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.defaultHandlePagination()
    }

    private deleteWorkflowTemplate = (id: string) => {
        this.setState(() => ({
            loading: true,
            selectedTemplate: null
        }), () => WorkflowService.deleteWorkflowTemplate(id, this.deleteWorkflowTemplateCallback, this.matesComponent.onException))
    }

    private deleteWorkflowTemplateCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.defaultHandlePagination()
    }

    private submitNewTemplate = (values: any) => {
        let request: NewWorkflowTemplate = {
            name: values.templateName,
            description: values.templateDescription,
            workflowObjectType: values.templateType,
            defaultExecutor: null
        }

        if (request.description === "") {
            request.description = null
        }

        if (request.defaultExecutor === "") {
            request.defaultExecutor = null
        }

        this.closeCreateNewTemplate()

        this.setState(() => ({ loading: true }))
        WorkflowService.createNewWorkflowTemplate(request, this.submitNewTemplateCallback, this.matesComponent.onException)
    }

    private submitNewTemplateCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.defaultHandlePagination()
    }

    private submitNewStep = (values: any) => {
        this.closeNewStep()

        this.setState(() => ({ loading: true }))
        WorkflowService.createNewStep(
            values,
            this.state.selectedTemplate!.id,
            this.submitNewStepCallback,
            this.matesComponent.onException,
        )
    }

    private submitNewStepCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }
        this.defaultHandlePagination()
    }

    private submitUpdateStep = (values: any) => {
        if (!this.state.editStep) {
            return
        }

        const selectedStepId = this.state.editStep.id

        this.closeEditStep()

        this.setState(() => ({ loading: true }))
        WorkflowService.updateStep(
            values,
            selectedStepId,
            this.submitUpdateStepCallback,
            this.matesComponent.onException,
        )
    }

    private submitUpdateStepCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }
        this.defaultHandlePagination()
    }

    private deleteStep = () => {
        if (!this.state.deleteStep) {
            return
        }

        const selectedStepId = this.state.deleteStep.id

        this.closeDeleteStep()

        this.setState(() => ({ loading: true }))
        WorkflowService.deleteStep(selectedStepId, this.deleteStepCallback, this.matesComponent.onException)
    }

    private deleteStepCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }
        this.defaultHandlePagination()
    }

    private cloneStep = (step: TemplateStep) => {
        if (!this.state.templateStepsResponse) {
            return
        }

        const selectedStepId = step.id

        this.setState(() => ({ loading: true }))
        WorkflowService.cloneStep(
            selectedStepId,
            this.cloneStepCallback,
            this.matesComponent.onException
        )
    }

    private cloneStepCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }
        this.defaultHandlePagination()
    }

    private assignTemplate = (templateId: string, userId: string) => {
        let request: AssignRequest = {
            executor: userId
        }

        WorkflowService.assignTemplate(templateId, request, this.assignTemplateCallback, this.matesComponent.onError)
    }

    private assignTemplateCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.defaultHandlePagination()
    }

    render() {
        return (
            <>
                <Loading show={this.state.loading} />
                {this.props.templatesData &&
                    <Grid container alignItems={"start"} columnSpacing={3} height={"65vh"}>
                        <Grid item xs={12} md={6}>
                            <TemplateColumnOne
                                handlePagination={this.handlePagination}
                                onItemSelected={this.onItemSelected}
                                openAssignTemplate={this.openAssignTemplate}
                                openDeleteTemplate={this.openDeleteTemplate}
                                setDefault={this.setDefault}
                                cloneWorkflowTemplate={this.cloneWorkflowTemplate}
                                openCreateNewTemplate={this.openCreateNewTemplate}
                                createWorkflowFromTemplate={this.createWorkflowFromTemplate}
                                selectedTemplateId={this.state.selectedTemplate ? this.state.selectedTemplate.id : null}
                                templates={this.props.templatesData} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {this.state.templateStepsResponse !== null &&
                                <TemplateColumnTwo
                                    openDeleteStep={this.openDeleteStep}
                                    openEditStep={this.openEditStep}
                                    cloneStep={this.cloneStep}
                                    openNewStep={this.openNewStep}
                                    steps={this.state.templateStepsResponse.steps} />
                            }
                        </Grid>
                    </Grid>
                }
                {
                    this.state.openNewTemplate &&
                    <NewTemplate
                        openDialog={this.state.openNewTemplate}
                        onClose={this.closeCreateNewTemplate}
                        submit={this.submitNewTemplate}
                        users={this.props.usersData}
                    />
                }

                {
                    this.state.openNewStep && this.state.selectedTemplate &&
                    <NewStep
                        openDialog={this.state.openNewStep}
                        onClose={this.closeNewStep}
                        data={null}
                        submit={this.submitNewStep}
                        create
                        stepOrder={this.state.templateStepsResponse ? this.state.templateStepsResponse.steps.length + 1 : 0}
                        workflowType={this.state.selectedTemplate.objectType}
                    />
                }
                {this.state.deleteTemplate &&
                    <DeleteWorkflow
                        openDialog={this.state.openDeleteTemplate}
                        onClose={this.closeDeleteTemplate}
                        data={this.state.deleteTemplate}
                        delete={this.deleteWorkflowTemplate}
                    />
                }
                {this.state.assignTemplate && this.props.usersData &&
                    <AssignTemplate
                        openDialog={this.state.openAssignTemplate}
                        onClose={this.closeAssignTemplate}
                        data={this.state.assignTemplate}
                        users={this.props.usersData}
                        onAssign={this.assignTemplate}
                    />
                }
                {this.state.deleteStep &&
                    <DeleteStep
                        openDialog={this.state.openDeleteStep}
                        onClose={this.closeDeleteStep}
                        data={this.state.deleteStep.name}
                        onDelete={this.deleteStep}
                    />
                }
                {this.state.openEditStep && this.state.selectedTemplate &&
                    <NewStep
                        openDialog={this.state.openEditStep}
                        onClose={this.closeEditStep}
                        stepData={this.state.editStep}
                        submit={this.submitUpdateStep}
                        workflowType={this.state.selectedTemplate.objectType}
                        edit
                    />
                }
            </>
        )
    }
}

export default withUseRouter(TemplateSection)
